/**
 * Clínica universitaria: el estudiante atiende y el tutor aprueba cada paso (historia clínica, plan de tratamiento,
 * autorización de cada procedimiento, validación con rúbrica y alta del paciente). Los procedimientos validados
 * cuentan para los requisitos mínimos de la clínica y del nivel del estudiante.
 */

export const TIPOS_SOLICITUD = ["historia", "plan", "autorizacion", "validacion", "alta"] as const;
export type TipoSolicitud = (typeof TIPOS_SOLICITUD)[number];
export const NOMBRE_SOLICITUD: Readonly<Record<TipoSolicitud, string>> = {
  historia: "Historia clínica", plan: "Plan de tratamiento", autorizacion: "Autorización del procedimiento",
  validacion: "Validación del procedimiento", alta: "Alta del paciente",
};
export type EstadoSolicitud = "pendiente" | "aprobado" | "devuelto" | "anulado";

/** Código especial de requisito: pacientes dados de alta. */
export const CODIGO_ALTA = "ALTA";

export interface Requisito { nombre: string; codigos: string[]; minimo: number }
export interface Criterio { nombre: string; maximo: number }
export interface Rubrica { nombre: string; criterios: Criterio[]; notaMinima: number; codigos: string[] }

/* ---------- rúbricas ---------- */

export function problemasRubrica(r: Rubrica): string[] {
  const e: string[] = [];
  if (!r.nombre.trim() || r.nombre.trim().length < 2) e.push("Ponle un nombre a la rúbrica.");
  if (!r.criterios.length) e.push("Agrega al menos un criterio.");
  if (r.criterios.length > 20) e.push("Máximo 20 criterios por rúbrica.");
  r.criterios.forEach((c, k) => {
    if (!c.nombre.trim()) e.push(`Criterio ${k + 1}: falta el nombre.`);
    if (!Number.isFinite(c.maximo) || c.maximo <= 0 || c.maximo > 100) e.push(`Criterio ${k + 1}: el puntaje máximo va de 0.5 a 100.`);
  });
  if (!Number.isFinite(r.notaMinima) || r.notaMinima < 0 || r.notaMinima > 10) e.push("La nota mínima va de 0 a 10.");
  return e;
}

/** Nota sobre 10 a partir de los puntajes de cada criterio (redondeada a 2 decimales). */
export function notaRubrica(r: Pick<Rubrica, "criterios" | "notaMinima">, puntajes: number[]): { nota: number; aprueba: boolean; errores: string[] } {
  const errores: string[] = [];
  if (puntajes.length !== r.criterios.length) errores.push("Califica todos los criterios de la rúbrica.");
  let total = 0, maximo = 0;
  r.criterios.forEach((c, k) => {
    const v = puntajes[k];
    if (v === undefined || v === null || !Number.isFinite(v)) { errores.push(`Falta el puntaje de «${c.nombre}».`); return; }
    if (v < 0 || v > c.maximo) errores.push(`«${c.nombre}»: el puntaje va de 0 a ${c.maximo}.`);
    total += Math.min(Math.max(v, 0), c.maximo); maximo += c.maximo;
  });
  const nota = maximo ? Math.round((total / maximo) * 1000) / 100 : 0;
  return { nota, aprueba: !errores.length && nota >= r.notaMinima, errores };
}

/* ---------- requisitos y avance ---------- */

export function problemasRequisitos(rs: Requisito[]): string[] {
  const e: string[] = [];
  if (rs.length > 40) e.push("Máximo 40 requisitos por clínica.");
  rs.forEach((r, k) => {
    const n = `Requisito ${k + 1}`;
    if (!r.nombre.trim()) e.push(`${n}: falta el nombre.`);
    if (!r.codigos.length) e.push(`${n}: elige al menos un procedimiento que cuente.`);
    if (r.codigos.some((c) => !/^[A-Z0-9]{1,10}$/.test(c))) e.push(`${n}: hay un código de procedimiento inválido.`);
    if (!Number.isInteger(r.minimo) || r.minimo < 1 || r.minimo > 500) e.push(`${n}: el mínimo va de 1 a 500.`);
  });
  return e;
}

export interface Hecho { codigo: string; cantidad: number }
export interface Avance { nombre: string; codigos: string[]; minimo: number; hechos: number; cumplido: boolean }

/** Avance de cada requisito con los procedimientos validados (y las altas). */
export function avanceRequisitos(rs: readonly Requisito[], hechos: readonly Hecho[]): { requisitos: Avance[]; porcentaje: number; cumplidos: number } {
  const requisitos = rs.map((r) => {
    const n = hechos.filter((h) => r.codigos.includes(h.codigo)).reduce((s, h) => s + h.cantidad, 0);
    return { nombre: r.nombre, codigos: r.codigos, minimo: r.minimo, hechos: n, cumplido: n >= r.minimo };
  });
  const total = requisitos.reduce((s, r) => s + r.minimo, 0);
  const logrado = requisitos.reduce((s, r) => s + Math.min(r.hechos, r.minimo), 0);
  return { requisitos, porcentaje: total ? Math.round((logrado / total) * 100) : 0, cumplidos: requisitos.filter((r) => r.cumplido).length };
}

/* ---------- flujo del caso ---------- */

export interface EstadoPaso { aprobado: boolean; pendiente: boolean; devuelto: boolean }
export interface ResumenCaso {
  historia: EstadoPaso; plan: EstadoPaso; alta: EstadoPaso;
  /** por procedimiento del plan */
  items: Record<string, { autorizado: boolean; validado: boolean; pendiente: TipoSolicitud | null; ultimaObservacion: string; nota: number | null }>;
}
export interface SolicitudResumen { tipo: TipoSolicitud; estado: EstadoSolicitud; plan_item_id: string | null; observacion: string; nota: number | null }

/** Estado de cada paso del caso a partir de sus solicitudes (en orden de creación). */
export function resumenCaso(sols: readonly SolicitudResumen[]): ResumenCaso {
  const paso = (t: TipoSolicitud): EstadoPaso => {
    const de = sols.filter((s) => s.tipo === t && s.estado !== "anulado");
    const ult = de[de.length - 1];
    return { aprobado: de.some((s) => s.estado === "aprobado"), pendiente: ult?.estado === "pendiente", devuelto: ult?.estado === "devuelto" };
  };
  const items: ResumenCaso["items"] = {};
  for (const s of sols) {
    if (!s.plan_item_id || s.estado === "anulado") continue;
    const x = items[s.plan_item_id] ??= { autorizado: false, validado: false, pendiente: null, ultimaObservacion: "", nota: null };
    if (s.estado === "aprobado" && s.tipo === "autorizacion") x.autorizado = true;
    if (s.estado === "aprobado" && s.tipo === "validacion") { x.validado = true; x.nota = s.nota; }
    if (s.estado === "pendiente") x.pendiente = s.tipo;
    if (s.estado === "devuelto") { x.ultimaObservacion = s.observacion; if (s.tipo === "validacion") x.nota = s.nota; }
  }
  return { historia: paso("historia"), plan: paso("plan"), alta: paso("alta"), items };
}

/**
 * ¿Puede el estudiante pedir este paso ahora? Devuelve el motivo si no.
 * Orden: historia → plan → (autorización → validación) por procedimiento → alta.
 */
export function puedeSolicitar(r: ResumenCaso, tipo: TipoSolicitud, item?: { id: string; realizado: boolean } | null, pendientesPlan = 0): string | null {
  if (tipo === "historia") return r.historia.aprobado ? "La historia clínica ya está aprobada." : r.historia.pendiente ? "La historia ya está en revisión." : null;
  if (!r.historia.aprobado) return "Primero el tutor debe aprobar la historia clínica.";
  if (tipo === "plan") return r.plan.aprobado ? "El plan ya está aprobado." : r.plan.pendiente ? "El plan ya está en revisión." : null;
  if (!r.plan.aprobado) return "Primero el tutor debe aprobar el plan de tratamiento.";
  if (tipo === "alta") {
    if (r.alta.aprobado) return "El paciente ya fue dado de alta.";
    if (r.alta.pendiente) return "El alta ya está en revisión.";
    if (pendientesPlan > 0) return `Quedan ${pendientesPlan} procedimiento(s) del plan sin validar.`;
    return null;
  }
  if (!item) return "Elige el procedimiento del plan.";
  const x = r.items[item.id];
  if (x?.pendiente) return "Ese procedimiento ya tiene una solicitud en revisión.";
  if (tipo === "autorizacion") return x?.autorizado ? "Ese procedimiento ya está autorizado." : item.realizado ? "Ese procedimiento ya está realizado." : null;
  // validación
  if (!x?.autorizado) return "Primero pide la autorización del tutor para ese procedimiento.";
  if (x.validado) return "Ese procedimiento ya está validado.";
  return null;
}

/* ---------- plantilla de ejemplo ---------- */

/**
 * Plantilla de EJEMPLO para empezar la demostración. No corresponde al sílabo de ninguna universidad:
 * cada facultad la reemplaza por sus clínicas, niveles y mínimos reales.
 */
export const PLANTILLA_ACADEMICA: readonly { nombre: string; nivel: number; descripcion: string; requisitos: Requisito[] }[] = [
  {
    nombre: "Clínica Integral I", nivel: 7, descripcion: "Ejemplo: prevención, operatoria y periodoncia básica.",
    requisitos: [
      { nombre: "Profilaxis y destartraje", codigos: ["PRO"], minimo: 5 },
      { nombre: "Sellantes", codigos: ["SEL"], minimo: 4 },
      { nombre: "Resinas", codigos: ["RES1", "RES2", "RES3"], minimo: 8 },
      { nombre: "Raspado y alisado (cuadrantes)", codigos: ["RAS"], minimo: 4 },
      { nombre: "Exodoncias simples", codigos: ["EXO"], minimo: 3 },
      { nombre: "Pacientes dados de alta", codigos: [CODIGO_ALTA], minimo: 2 },
    ],
  },
  {
    nombre: "Clínica Integral II", nivel: 8, descripcion: "Ejemplo: endodoncia y rehabilitación.",
    requisitos: [
      { nombre: "Resinas", codigos: ["RES1", "RES2", "RES3"], minimo: 10 },
      { nombre: "Endodoncias unirradiculares", codigos: ["END1"], minimo: 2 },
      { nombre: "Endodoncias bi o multirradiculares", codigos: ["END2", "END3"], minimo: 1 },
      { nombre: "Pernos muñón", codigos: ["PER"], minimo: 1 },
      { nombre: "Coronas", codigos: ["COR", "CZR"], minimo: 1 },
      { nombre: "Pacientes dados de alta", codigos: [CODIGO_ALTA], minimo: 3 },
    ],
  },
  {
    nombre: "Clínica de Niños", nivel: 8, descripcion: "Ejemplo: odontopediatría.",
    requisitos: [
      { nombre: "Barniz de flúor", codigos: ["FLU"], minimo: 5 },
      { nombre: "Sellantes", codigos: ["SEL"], minimo: 6 },
      { nombre: "Ionómeros y resinas", codigos: ["ION", "RES1", "RES2"], minimo: 4 },
      { nombre: "Pulpotomías", codigos: ["PPT"], minimo: 2 },
      { nombre: "Pulpectomías", codigos: ["PPC"], minimo: 1 },
      { nombre: "Coronas de acero", codigos: ["CA"], minimo: 2 },
      { nombre: "Mantenedores de espacio", codigos: ["ME"], minimo: 1 },
    ],
  },
  {
    nombre: "Clínica de Cirugía", nivel: 9, descripcion: "Ejemplo: cirugía oral.",
    requisitos: [
      { nombre: "Exodoncias simples", codigos: ["EXO"], minimo: 8 },
      { nombre: "Exodoncias quirúrgicas o terceros molares", codigos: ["EXQ"], minimo: 3 },
    ],
  },
];

/** Rúbrica general de EJEMPLO para validar procedimientos (cada tutor puede crear la suya). */
export const RUBRICA_EJEMPLO: Rubrica = {
  nombre: "Procedimiento clínico (ejemplo)", notaMinima: 7, codigos: [],
  criterios: [
    { nombre: "Bioseguridad y preparación del campo", maximo: 2 },
    { nombre: "Diagnóstico y planificación del procedimiento", maximo: 2 },
    { nombre: "Técnica y ejecución", maximo: 4 },
    { nombre: "Resultado final y control", maximo: 1 },
    { nombre: "Registro en la historia y trato al paciente", maximo: 1 },
  ],
};

/* ---------- pacientes del estudiante ---------- */

/**
 * El paciente es del estudiante hasta que se gradúa. Se cede a otro estudiante solo entre semestres (fuera de las
 * fechas del periodo activo), o en cualquier momento si el estudiante ya no está activo (graduado o retirado).
 */
export function motivoNoCeder(periodo: { inicio: string; fin: string } | null, hoy: string, duenoActivo: boolean): string | null {
  if (!duenoActivo) return null;
  if (periodo && hoy >= periodo.inicio && hoy <= periodo.fin)
    return `El semestre está en curso (hasta el ${periodo.fin}): el paciente se puede ceder a otro estudiante cuando termine.`;
  return null;
}
